-- | Initial app hydration and browser-location derivation.
-- |
-- | This module owns route parsing, route-to-state derivation, and bootstrap
-- | hydration for the app runtime.
module App.Hydrate
    ( loadAppStateWidget
    , loadAppState
    , deriveAppState
    , resolveRoute
    , routeForState
    ) where

import Common

import API.FormBase (FormBasic, FormID)
import API.FormType (emptyFormPickerMap, formListQuery, selectForms)
import API.RecentForms (fetchRecentFormsByIds)
import API.SelStudent (SelStudent, fetchStudentById)
import API.Types (StudentID)
import App.Browser (readLocation)
import App.Route as AppRoute
import App.State (AppState, initialAppState, syncStepSessions)
import Control.Alt ((<|>))
import Control.Monad.Except.Trans (ExceptT(..), lift, runExceptT)
import Data.Array as Arr
import Data.Traversable (sequence)
import Error (ErrorWrapper)
import GraphQL.Join (getDecode, getQuery, runGraphQL, runMultiQuery)
import Internal.SelectionMap as Sel
import App.Step (Step(..))
import Steps.WorkQueue.Cache (getDoc, initialWorkQueueCache, loadWorkQueue)
import Steps.WorkQueue.State (WorkQueueState, editDocsWorkQueue, getFillQueue, initialWorkQueueState, setCurrentDoc)
import Steps.WorkQueue.UIState (initialWorkQueueUiState)
import Widgets.Loading (loadingWidget)
import Routing.PushState (LocationState, makeInterface)

loadAppStateWidget :: Widget HTML (Either ErrorWrapper AppState)
loadAppStateWidget = loadingWidget <|> affAction loadAppState

loadAppState :: Aff (Either ErrorWrapper AppState)
loadAppState = runExceptT do
    pushStateInterface <- lift $ liftEffect makeInterface
    locationState <- lift $ liftEffect $ readLocation pushStateInterface

    workQueue <- ExceptT $ loadWorkQueue initialWorkQueueState initialWorkQueueUiState initialWorkQueueCache

    let initial = initialAppState
            { route: AppRoute.RHome { preservedQuery: AppRoute.preserveQuery locationState.search }
            , step: Step1
            , workQueueState: workQueue.state
            , workQueueUi: workQueue.ui
            , workQueueCache: workQueue.cache
            , preselectForms: []
            , pushStateInterface
            }

    ExceptT $ deriveAppState initial locationState

deriveAppState :: AppState -> LocationState -> Aff (Either ErrorWrapper AppState)
deriveAppState state locationState = runExceptT do
    let requestedRoute = AppRoute.parseRoute locationState

    Tuple step' workQueueState' <- ExceptT $ resolveRoute requestedRoute state.workQueueState

    let nextState0 = state
            { step = step'
            , workQueueState = workQueueState'
            , preselectForms = []
            }

    let nextState = nextState0 { route = routeForState locationState nextState0 }
    pure $ syncStepSessions state nextState

resolveRoute :: AppRoute.Route -> WorkQueueState -> Aff (Either ErrorWrapper (Tuple Step WorkQueueState))
resolveRoute (AppRoute.RStep3 { activeDoc: Just docId, docs }) workQueue =
    case getDoc workQueue docId of
        Just doc -> do
            let committed = setCurrentDoc workQueue $ Just
                    { formId: doc.formId
                    , studentId: doc.student.id
                    , documentId: Just doc.id
                    }
            pure $ Right $ Tuple (Step3Doc docId) committed
        Nothing -> do
            let requestedDocs = Arr.nub $ Arr.cons docId docs
            let existingDocs = Arr.filter (\form -> form.id `Arr.elem` requestedDocs) workQueue.editDocs
            let existingIds = map _.id existingDocs
            let missingDocs = Arr.filter (\doc -> not (doc `Arr.elem` existingIds)) requestedDocs

            fetchResult <- if Arr.null missingDocs
                then pure $ Right []
                else do
                    runGraphQL $ fetchRecentFormsByIds missingDocs

            case fetchResult of
                Left err -> pure $ Left err
                Right fetchedDocs -> do
                    let fetched = Arr.mapMaybe
                            (\requestedId -> Arr.find (\form -> form.id == requestedId) fetchedDocs)
                            requestedDocs
                    let combinedDocs = existingDocs <> fetched
                    case Arr.find (\form -> form.id == docId) combinedDocs of
                        Just form ->
                            let workQ = editDocsWorkQueue workQueue combinedDocs
                                current = Just
                                    { formId: form.form_type.id
                                    , studentId: form.student.id
                                    , documentId: Just form.id
                                    }
                                committed = setCurrentDoc workQ current
                            in pure $ Right $ Tuple (Step3Doc docId) committed
                        Nothing ->
                            pure $ Right $ Tuple Step1 workQueue

resolveRoute (AppRoute.RStep3 { activeDoc: Nothing }) workQueue
    | Arr.null (getFillQueue workQueue) = pure $ Right $ Tuple Step1 workQueue
    | otherwise = pure $ Right $ Tuple Step3 workQueue

resolveRoute (AppRoute.RStep2A { selectedForms }) workQueue = do
    result <- hydrateFormSelections workQueue selectedForms
    pure $ map (Tuple Step2A) result

resolveRoute (AppRoute.RStep2B { selectedForms, selectedStudents }) workQueue
    | Arr.null selectedStudents = do
        result <- hydrateFormSelections workQueue selectedForms
        pure $ map (Tuple Step2A) result
    | otherwise = do
        formResult <- hydrateFormSelections workQueue selectedForms
        case formResult of
            Left err -> pure $ Left err
            Right formWorkQ -> do
                studentResult <- hydrateStudentSelections formWorkQ selectedStudents
                pure $ map (Tuple Step2B) studentResult

resolveRoute route workQueue =
    pure $ Right $ Tuple (AppRoute.routeToStep route) workQueue

hydrateFormSelections :: WorkQueueState -> Array FormID -> Aff (Either ErrorWrapper WorkQueueState)
hydrateFormSelections workQueue formIds
    | Arr.null formIds = pure $ Right workQueue
    | otherwise = do
        formsResult <- runGraphQL formListQuery
        pure $ map (applyFormSelections formIds workQueue) formsResult

applyFormSelections :: Array FormID -> WorkQueueState -> Array FormBasic -> WorkQueueState
applyFormSelections formIds workQueue forms =
    let selected = Arr.filter (\form -> form.id `Arr.elem` formIds) forms
        selectedMap = selectForms selected true emptyFormPickerMap
    in workQueue { forms = selectedMap }

hydrateStudentSelections :: WorkQueueState -> Array StudentID -> Aff (Either ErrorWrapper WorkQueueState)
hydrateStudentSelections workQueue studentIds
    | Arr.null studentIds = pure $ Right workQueue
    | otherwise = do
        let queries = map fetchStudentById studentIds
        batchResult <- runExceptT $ runMultiQuery $ map getQuery queries
        case batchResult of
            Left err -> pure $ Left err
            Right responses ->
                pure $ map (applyStudentSelections workQueue) $ sequence $ Arr.zipWith getDecode queries responses

applyStudentSelections :: WorkQueueState -> Array SelStudent -> WorkQueueState
applyStudentSelections workQueue students =
    let selected = Sel.selectAll' students Sel.empty true
    in workQueue { students = selected }

routeForState :: LocationState -> AppState -> AppRoute.Route
routeForState locationState state =
    AppRoute.routeForStepAndWorkQueueState locationState state.step state.workQueueState
